import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { initializeApp } from 'firebase/app';
import { deleteToken, getMessaging, getToken } from 'firebase/messaging';
import { Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { firebaseConfig } from './../../../firebase-config';

@Injectable({
  providedIn: 'root',
})
export class PushNotificationService {
  private messaging;
  private iosLimitationDetectedSubject = new Subject<boolean>();
  public iosLimitationDetected$ =
    this.iosLimitationDetectedSubject.asObservable();
  private tokenRequestInProgress = false;

  constructor(private http: HttpClient) {
    const app = initializeApp(firebaseConfig);
    this.messaging = getMessaging(app);
  }

  isIOS(): boolean {
    return (
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
  }

  isStandaloneMode(): boolean {
    return (
      (window.navigator as any).standalone === true ||
      window.matchMedia('(display-mode: standalone)').matches
    );
  }

  shouldShowIOSInstructions(): boolean {
    if (!this.isIOS()) {
      return false;
    }
    if (this.isStandaloneMode()) {
      return false;
    }
    const dismissed = localStorage.getItem('ios-instruction-dismissed');
    return dismissed !== 'true';
  }

  requestPermissionAndSaveToken(userGuid: string) {
    if (this.isIOS() && !this.isStandaloneMode()) {
      this.iosLimitationDetectedSubject.next(true);
      return;
    }

    if (this.tokenRequestInProgress) {
      return;
    }

    this.tokenRequestInProgress = true;

    if ('serviceWorker' in navigator && 'PushManager' in window) {
      navigator.serviceWorker
        .getRegistration()
        .then((existingRegistration) => {
          if (existingRegistration && existingRegistration.active) {
            return Promise.resolve(existingRegistration);
          }
          return navigator.serviceWorker.register('/firebase-messaging-sw.js');
        })
        .then((registration) => {
          if (
            registration.active ||
            registration.installing ||
            registration.waiting
          ) {
            console.log('Service Worker registrado com sucesso:', registration);
          }
          return navigator.serviceWorker.ready;
        })
        .then((registration) => {
          return getToken(this.messaging, {
            vapidKey: `${environment.vapid_key}`,
            serviceWorkerRegistration: registration,
          });
        })
        .then((token) => {
          this.tokenRequestInProgress = false;
          if (token) {
            console.log('FCM Token:', token);
            this.sendTokenToBackend(userGuid, token);
          } else {
            console.warn(
              'Nenhum token disponível. Pode ser necessário pedir permissão para notificações.'
            );
            if (this.isIOS() && !this.isStandaloneMode()) {
              this.iosLimitationDetectedSubject.next(true);
            }
          }
        })
        .catch((err) => {
          this.tokenRequestInProgress = false;
          console.error('Erro ao pegar token: ', err);
          if (this.isIOS() && !this.isStandaloneMode()) {
            this.iosLimitationDetectedSubject.next(true);
          }
        });
    } else {
      this.tokenRequestInProgress = false;
      console.warn(
        'Service Worker ou PushManager não suportados no navegador.'
      );
      if (this.isIOS() && !this.isStandaloneMode()) {
        this.iosLimitationDetectedSubject.next(true);
      }
    }
  }

  private sendTokenToBackend(userGuid: string, token: string) {
    this.http
      .post(`${environment.url_api}auth/user/young/notifications/token`, {
        user_guid: userGuid,
        token: token,
        platform: 'web',
      })
      .subscribe({
        next: () => console.log('Token enviado para o backend com sucesso!'),
        error: (error) => console.error('Erro ao enviar token:', error),
      });
  }

  // 🔥 Aqui entra o código novo para deletar no logout:
  async removeTokenOnLogout() {
    try {
      const token = await getToken(this.messaging, {
        vapidKey: `${environment.vapid_key}`,
      });

      if (token) {
        await this.http
          .request(
            'delete',
            `${environment.url_api}auth/user/young/notifications/token`,
            { body: { token } }
          )
          .toPromise();

        console.log('Token removido no backend com sucesso');

        // Remove o token também do Firebase local (boa prática):
        deleteToken(this.messaging)
          .then(() => {
            console.log('Token removido localmente do Firebase');
          })
          .catch((err) => {
            console.error('Erro ao remover token local:', err);
          });
      } else {
        console.warn('Nenhum token encontrado para remover.');
      }
    } catch (error) {
      console.error('Erro ao obter token para remoção:', error);
    }
  }
}
